// frontend/src/hooks/useProviders.ts
// CLEANED VERSION - Removed bulk operations, follows current codebase patterns

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { providerService, ProviderFilters, ProviderFormData } from '@/lib/api/providerService';

// Query keys for consistency - matches current pattern
export const providerKeys = {
  all: ['providers'] as const,
  lists: () => [...providerKeys.all, 'list'] as const,
  list: (filters: ProviderFilters) => [...providerKeys.lists(), filters] as const,
  details: () => [...providerKeys.all, 'detail'] as const,
  detail: (id: number) => [...providerKeys.details(), id] as const,
  profile: () => [...providerKeys.all, 'profile'] as const,
  stats: () => [...providerKeys.all, 'stats'] as const,
} as const;

// Get all providers (admin view)
export const useProviders = (filters: ProviderFilters = {}) => {
  return useQuery({
    queryKey: providerKeys.list(filters),
    queryFn: async () => {
      const response = await providerService.getProviders(filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch providers');
    },
    staleTime: 300000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get providers with integrated stats (admin view) - Use this instead of separate calls
export const useProvidersWithStats = (filters: ProviderFilters = {}) => {
  return useQuery({
    queryKey: providerKeys.list(filters),
    queryFn: async () => {
      const response = await providerService.getProviders(filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch providers');
    },
    staleTime: 300000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
    select: (data) => {
      // Transform the data to separate providers and stats
      return {
        providers: data.providers || data.data || [],
        pagination: data.pagination || null,
        stats: data.stats || null
      };
    }
  });
};

// Get single provider by ID (admin view)
export const useProvider = (id: number) => {
  return useQuery({
    queryKey: providerKeys.detail(id),
    queryFn: async () => {
      const response = await providerService.getProviderById(id);
      if (response.success && response.data) {
        return response.data.provider;
      }
      throw new Error(response.message || 'Failed to fetch provider');
    },
    enabled: !!id,
    staleTime: 300000,
  });
};

// Get current provider profile (provider dashboard view)
export const useProviderProfile = () => {
  return useQuery({
    queryKey: providerKeys.profile(),
    queryFn: async () => {
      const response = await providerService.getCurrentProvider();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch provider profile');
    },
    staleTime: 300000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Create provider (admin action)
export const useCreateProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ProviderFormData) => {
      const response = await providerService.createProvider(data);
      if (!response.success) {
        const error = new Error(response.message || 'Failed to create provider');
        // Only add errors if they exist in the response
        if (response.data && typeof response.data === 'object' && 'errors' in response.data) {
          (error as any).errors = (response.data as any).errors;
        }
        throw error;
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
    },
    onError: (error: any) => {
      console.error('Create provider error:', error);
    }
  });
};

// Update provider (admin action)
export const useUpdateProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ProviderFormData }) => {
      const response = await providerService.updateProvider(id, data);
      if (!response.success) {
        const error = new Error(response.message || 'Failed to update provider');
        // Only add errors if they exist in the response
        if (response.data && typeof response.data === 'object' && 'errors' in response.data) {
          (error as any).errors = (response.data as any).errors;
        }
        throw error;
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
      queryClient.invalidateQueries({ queryKey: providerKeys.detail(variables.id) });
    },
    onError: (error: any) => {
      console.error('Update provider error:', error);
    }
  });
};

// Delete provider (admin action)
export const useDeleteProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (providerId: number) => {
      const response = await providerService.deleteProvider(providerId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete provider');
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
    },
    onError: (error: any) => {
      console.error('Delete provider error:', error);
    }
  });
};

// Verify provider (admin action)
export const useVerifyProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (providerId: number) => {
      const response = await providerService.verifyProvider(providerId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to verify provider');
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
      queryClient.invalidateQueries({ queryKey: providerKeys.detail(variables) });
    },
    onError: (error: any) => {
      console.error('Verify provider error:', error);
    }
  });
};

// Toggle provider status (admin action)
export const useToggleProviderStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (providerId: number) => {
      const response = await providerService.toggleProviderStatus(providerId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to toggle provider status');
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: providerKeys.all });
      queryClient.invalidateQueries({ queryKey: providerKeys.detail(variables) });
    },
    onError: (error: any) => {
      console.error('Toggle provider status error:', error);
    }
  });
};

// Get provider statistics (admin view)
export const useProviderStats = () => {
  return useQuery({
    queryKey: providerKeys.stats(),
    queryFn: async () => {
      const response = await providerService.getProviderStats();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch provider statistics');
    },
    staleTime: 600000, // 10 minutes
  });
};

// Update provider profile (provider action)
export const useUpdateProviderProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await providerService.updateProviderProfile(data);
      if (!response.success) {
        const error = new Error(response.message || 'Failed to update profile');
        // Only add errors if they exist in the response
        if (response.data && typeof response.data === 'object' && 'errors' in response.data) {
          (error as any).errors = (response.data as any).errors;
        }
        throw error;
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.profile() });
    },
    onError: (error: any) => {
      console.error('Update provider profile error:', error);
    }
  });
};

